// Exportación en PDF de una fecha: encabezado con el torneo y el año, la zona y
// la fecha (número y día de calendario), la tabla de posiciones acumulada hasta
// esa fecha y, debajo, los resultados de todas las fechas jugadas hasta esa,
// de la más reciente a la más antigua.
//
// El PDF es para mirar en pantalla, no para imprimir: sale en UNA hoja continua,
// del ancho de una A4 y del alto que haga falta, y todo va de corrido (ninguna
// tabla arranca hoja nueva). Como jsPDF fija el alto de la hoja al crearla y
// posiciona el contenido contra ese alto, el contenido se dibuja dos veces: la
// primera sobre una hoja gigante, solo para medir dónde termina, y la segunda
// sobre la hoja definitiva, del alto justo.
//
// La tabla de posiciones sale de `computeStandings`, la misma que dibuja
// StandingsTable en pantalla, así que el PDF muestra exactamente lo que se ve al
// cargar los resultados. Las filas que quedaron igualadas (las que la tabla
// resalta en ámbar) se resaltan igual acá.
//
// Los resultados van en una tabla por fecha, encabezada por su rótulo ("Fecha 3 ·
// Lunes 3 de agosto"). Las tablas no llevan encabezado de columnas: cada partido
// es un equipo de cada lado con el marcador en negrita en el medio. El orden de
// las fechas (y su numeración) es el de components/torneos/fechas.js, el mismo
// con el que la tabla de posiciones acumula, así que "hasta la Fecha N" significa
// lo mismo en las dos partes del PDF.
//
// Los partidos que todavía no se jugaron se listan igual, con el marcador en
// blanco: el PDF es la foto de la zona a esa fecha, con lo cargado y lo que falta.
//
// jspdf/jspdf-autotable se cargan con import() dinámico para que no entren en el
// bundle principal de la PWA: solo se bajan cuando se toca el botón.

import { etiquetaFecha, fechaCalendario, numerosDeFecha, ordenarFechas } from "components/torneos/fechas";
import { porCanchaYHora } from "components/torneos/horarios";
import { esWO, esWODoble, marcadorDePartido } from "components/torneos/resultado";
import { computeStandings } from "./standings";

// Hoja continua: el ancho es el de una A4 y el alto lo pone el contenido.
const ANCHO_HOJA = 210;
const MARGEN = 14;
const ANCHO_UTIL = ANCHO_HOJA - MARGEN * 2;
// Alto de la hoja de la pasada de medición: cualquier cosa que no vaya a llenarse.
const ALTO_MEDICION = 5000;
// Tope del formato PDF (14400 pt). Un PDF más largo que esto se pagina solo.
const ALTO_MAXIMO = 5080;
// Piso del alto: una A4. Sirve para que un PDF corto se vea como una hoja normal
// y, sobre todo, para que jsPDF no dé vuelta la hoja (en "portrait" invierte los
// lados cuando el alto es menor que el ancho).
const ALTO_MINIMO = 297;

// Pts, PJ, PG, PE, PP, GF, GC, Dif
const COLUMNAS_NUMERICAS = 8;

// Fondo ámbar de las filas empatadas, el mismo de .standings-tied en pantalla.
const COLOR_EMPATADOS = [253, 231, 184];

const SIN_MARCADOR = "—";

const FONT_BODY = 9;
const FONT_HEAD = 8;
const FONT_SECCION = 11;
const FONT_FECHA = 10;
const PADDING = 1.6;

// Lo que baja el título de una sección (o el rótulo de una fecha) hasta su tabla.
const ALTO_TITULO = 3;
// Aire entre la tabla de posiciones y el título "RESULTADOS", entre ese título y
// el rótulo de la primera fecha, y entre una fecha y el rótulo de la siguiente.
const AIRE_ENTRE_SECCIONES = 12;
const AIRE_TRAS_SECCION = 3.5;
const AIRE_ENTRE_FECHAS = 6;

// Columnas de la tabla de posiciones.
const ANCHO_PUESTO = 9;
const ANCHO_NUMERICA = 12;
const ANCHO_EQUIPO_POSICIONES = ANCHO_UTIL - ANCHO_PUESTO - ANCHO_NUMERICA * COLUMNAS_NUMERICAS;
// Columnas de la lista de partidos: un equipo de cada lado y el marcador al medio.
const ANCHO_MARCADOR = 26;
const ANCHO_EQUIPO_RESULTADO = (ANCHO_UTIL - ANCHO_MARCADOR) / 2;

const limpiarNombreArchivo = (texto) => texto.replace(/[\\/:*?"<>|]/g, "-").replace(/\s+/g, " ").trim();

// Rótulo del walk-over: "DWO" cuando lo dieron los dos equipos.
const rotuloWO = (match) => (esWODoble(match) ? "DWO" : esWO(match) ? "WO" : "");

// "3 - 0 (WO)". Un partido sin jugar todavía no tiene marcador.
function textoDeMarcador(match) {
  if (!match.jugado) return SIN_MARCADOR;
  const marcador = marcadorDePartido(match);
  const wo = rotuloWO(match);
  return marcador.local + " - " + marcador.visitante + (wo ? " (" + wo + ")" : "");
}

// Las fechas de la zona hasta la seleccionada (inclusive), de la más reciente a la
// más antigua y ya sin las que no tienen partidos cargados.
function fechasConResultados(zona, fechaId) {
  const ordenadas = ordenarFechas(zona.matchDates || []);
  const indice = ordenadas.findIndex((f) => f.id === fechaId);
  const hastaLaFecha = indice >= 0 ? ordenadas.slice(0, indice + 1) : ordenadas;
  const numeros = numerosDeFecha(zona.matchDates || []);
  return hastaLaFecha
    .filter((f) => (f.matches || []).length > 0)
    .reverse()
    .map((f) => ({
      rotulo: etiquetaFecha(f, numeros[f.id]),
      partidos: [...f.matches].sort(porCanchaYHora),
    }));
}

// torneoNombre / torneoAno: encabezan el PDF ("TORNEO APERTURA 2026").
// zona: la zona completa (de ahí salen el nombre y las fechas para las posiciones).
// fecha: la fecha seleccionada. numero: su "Fecha N". teams: equipos del torneo.
export async function generarResultadosFecha({ torneoNombre, torneoAno, zona, fecha, numero, teams = [] }) {
  if (!zona || !fecha) return;

  const nombreDeEquipo = (teamId) => {
    const t = teams.find((x) => x.id === teamId);
    return t ? t.name : "—";
  };

  const posiciones = computeStandings(zona, fecha.id);
  const fechas = fechasConResultados(zona, fecha.id);

  const nombreDelTorneo = [torneoNombre || "Torneo", torneoAno].filter(Boolean).join(" ");
  const rotuloDeFecha = numero ? "Fecha " + numero : "Fecha";
  const diaDeFecha = fechaCalendario(fecha);

  const [{ jsPDF }, { default: autoTable }] = await Promise.all([
    import("jspdf"),
    import("jspdf-autotable"),
  ]);

  const estilos = {
    styles: {
      font: "helvetica",
      fontSize: FONT_BODY,
      cellPadding: PADDING,
      valign: "middle",
      lineColor: [90, 90, 90],
      lineWidth: 0.2,
      textColor: [0, 0, 0],
    },
    headStyles: {
      fontStyle: "bold",
      fontSize: FONT_HEAD,
      halign: "center",
      fillColor: [225, 225, 225],
      textColor: [0, 0, 0],
    },
  };

  const columnStylesPosiciones = {
    0: { cellWidth: ANCHO_PUESTO, halign: "center", textColor: [110, 110, 110] },
    1: { cellWidth: ANCHO_EQUIPO_POSICIONES },
    2: { fontStyle: "bold" },
  };
  for (let i = 2; i < 2 + COLUMNAS_NUMERICAS; i++)
    columnStylesPosiciones[i] = { ...columnStylesPosiciones[i], cellWidth: ANCHO_NUMERICA, halign: "center" };

  const filasDePosiciones =
    posiciones.length === 0
      ? [[{ content: "No hay equipos en la zona.", colSpan: 10, styles: { halign: "center" } }]]
      : posiciones.map((r, indice) => [
          indice + 1,
          nombreDeEquipo(r.teamId),
          r.pts,
          r.pj,
          r.pg,
          r.pe,
          r.pp,
          r.gf,
          r.gc,
          r.dif > 0 ? "+" + r.dif : r.dif,
        ]);

  // Dibuja todo el contenido de corrido y devuelve el alto que ocupó. Se llama una
  // vez para medir (sobre una hoja gigante) y otra para valer (sobre la definitiva).
  const dibujar = (doc) => {
    const centro = ANCHO_HOJA / 2;
    const margenes = { top: MARGEN, bottom: MARGEN, left: MARGEN, right: MARGEN };

    // ---- Encabezado ----
    let y = 20;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.setTextColor(0, 0, 0);
    doc.text(nombreDelTorneo.toUpperCase(), centro, y, { align: "center" });

    y += 7;
    doc.setFontSize(12);
    doc.text((zona.name || "Zona").toUpperCase(), centro, y, { align: "center" });

    y += 6;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(110, 110, 110);
    doc.text([rotuloDeFecha, diaDeFecha].filter(Boolean).join(" · "), centro, y, { align: "center" });
    doc.setTextColor(0, 0, 0);

    y += 4;
    doc.setDrawColor(180, 180, 180);
    doc.line(MARGEN, y, ANCHO_HOJA - MARGEN, y);
    y += 10;

    const rotular = (texto, fontSize) => {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(fontSize);
      doc.setTextColor(0, 0, 0);
      doc.text(texto, MARGEN, y);
      y += ALTO_TITULO;
    };

    // ---- Tabla de posiciones ----
    rotular("TABLA DE POSICIONES", FONT_SECCION);

    autoTable(doc, {
      startY: y,
      margin: margenes,
      head: [["#", "EQUIPO", "Pts", "PJ", "PG", "PE", "PP", "GF", "GC", "Dif"]],
      body: filasDePosiciones,
      theme: "grid",
      ...estilos,
      columnStyles: columnStylesPosiciones,
      // Los equipos que quedaron igualados (ni el head-to-head los separó) van
      // resaltados, igual que en la tabla de la pantalla.
      didParseCell: (data) => {
        if (data.section !== "body") return;
        const fila = posiciones[data.row.index];
        if (fila && fila.tied) data.cell.styles.fillColor = COLOR_EMPATADOS;
      },
    });

    y = doc.lastAutoTable.finalY + AIRE_ENTRE_SECCIONES;

    // ---- Resultados, una tabla por fecha (de la más reciente a la más antigua) ----
    rotular("RESULTADOS", FONT_SECCION);

    if (fechas.length === 0) {
      y += AIRE_TRAS_SECCION;
      autoTable(doc, {
        startY: y,
        margin: margenes,
        body: [[{ content: "No hay partidos cargados.", styles: { halign: "center" } }]],
        theme: "grid",
        ...estilos,
      });
      return doc.lastAutoTable.finalY;
    }

    fechas.forEach(({ rotulo, partidos }, indice) => {
      y += indice === 0 ? AIRE_TRAS_SECCION : AIRE_ENTRE_FECHAS;
      rotular(rotulo, FONT_FECHA);

      autoTable(doc, {
        startY: y,
        margin: margenes,
        body: partidos.map((m) => [
          nombreDeEquipo(m.homeTeamId),
          textoDeMarcador(m),
          nombreDeEquipo(m.awayTeamId),
        ]),
        theme: "grid",
        ...estilos,
        columnStyles: {
          0: { cellWidth: ANCHO_EQUIPO_RESULTADO, halign: "right" },
          1: { cellWidth: ANCHO_MARCADOR, halign: "center", fontStyle: "bold" },
          2: { cellWidth: ANCHO_EQUIPO_RESULTADO },
        },
      });

      y = doc.lastAutoTable.finalY;
    });

    return y;
  };

  const hojaDe = (alto) => new jsPDF({ orientation: "portrait", unit: "mm", format: [ANCHO_HOJA, alto] });

  const altoDelContenido = dibujar(hojaDe(ALTO_MEDICION)) + MARGEN;
  const doc = hojaDe(Math.min(Math.max(altoDelContenido, ALTO_MINIMO), ALTO_MAXIMO));
  dibujar(doc);

  const nombreArchivo = limpiarNombreArchivo(
    ["Resultados", nombreDelTorneo, zona.name || "", rotuloDeFecha, fecha.date || ""].filter(Boolean).join(" - ")
  );

  doc.save(nombreArchivo + ".pdf");
}
